import { create } from 'zustand';
import { navigate } from '@reach/router';
import moment from 'moment';
import axios from 'axios';
import mixpanel from 'mixpanel-browser';
import { isEmpty } from 'lodash';

import { client as apolloClient } from '../util/apolloClient';
import { client as api2Client } from '../util/api2Client';
import {
  getCauzesQuery,
  createPlannedEventMutation,
  updatePlannedEventMutation,
  updateCauzeMutation,
} from '../graphql/cauze';
import {
  createMatchMutation,
  editMatchMutation,
  updateCauzeMatchMutation,
} from '../graphql/match';
import { eventQuery, eventQueryApi2 } from '../graphql/event';
import uiStore from './UiStore';
import profileStore from './ProfileStore';
import { createVideoMutation } from 'graphql/content';
import { getToken } from '../util/storage';
import config from '../config';

const UPLOAD_URL = `${config.UPLOAD_ROOT}/event/image`;
const UPLOAD_URLV2 = `${config.API_ROOTV2}/api`;
const CAUZE_IMAGE_FORM_KEY = 'image';
const EVENT_DEFAULT_DAY_RANGE = 14;

const createInitialCauze = () => ({
  name: '',
  charities: [],
  eventState: 'UNPUBLISHED',
  description: '',
  startDate: '',
  endDate: '',
  imageId: '',
  previewImageUrl: '',
  hasDates: false,
  userMatchLimit: 5000,
  matchTotal: 0,
  hasMatch: false,
  hadPreviousMatch: false,
  previousMatchTotal: null,
  previewUrl: '',
  matchType: 'EVENT',
  matchMultiplier: 1,
  matchId: '',
});

const useCauzeStore = create((set, get) => ({
  cauzes: [],
  cauzesJoinTotal: 0,
  cauzesAmountTotal: 0,
  cauzesLoading: true,
  isError: false,
  error: null,
  isUploadingCauzeImage: false,
  imageUploadError: false,
  menuActiveForCharityId: null,
  matchHasChanged: false,
  cauzeTotals: new Map(),
  videoData: null,
  loadingCauze: false,
  loadingCauzeError: false,
  savingCauze: false,
  createCauzeError: false,
  currentCauze: createInitialCauze(),

  getInitial: async ({ userContext }) => {
    set({ cauzesLoading: true });

    const options = {
      variables: { userContext },
      query: getCauzesQuery,
      fetchPolicy: 'no-cache',
      errorPolicy: global.IS_LOCAL_OR_DEV ? 'all' : 'none',
    };

    try {
      const res = await apolloClient.query(options);
      const cauzes = res.data.eventsForContext
        .slice()
        .sort(
          (a, b) => moment(b.insertedAt).unix() - moment(a.insertedAt).unix(),
        );

      const cauzesAmountTotal = res.data.eventsForContext.reduce(
        (acc, { current }) => acc + current,
        0,
      );
      const cauzesJoinTotal = res.data.eventsForContext.reduce(
        (acc, { purchases }) => acc + purchases.length,
        0,
      );

      set({
        cauzes,
        cauzesAmountTotal,
        cauzesJoinTotal,
        cauzesLoading: false,
        isError: false,
        error: null,
      });
    } catch (err) {
      if (global.IS_LOCAL_OR_DEV) {
        // eslint-disable-next-line no-console
        console.error(err);
      }
      set({
        cauzesLoading: false,
        isError: true,
        error: err,
      });
    }
  },

  fetchTotals: async ({ userContext, ignoreCache = false }) => {
    const key = JSON.stringify(userContext);
    const { cauzeTotals } = get();

    if (!ignoreCache && cauzeTotals.get(key)) {
      return cauzeTotals.get(key);
    }

    const options = {
      variables: { userContext },
      query: getCauzesQuery,
      fetchPolicy: 'no-cache',
      errorPolicy: global.IS_LOCAL_OR_DEV ? 'all' : 'none',
    };

    try {
      const res = await apolloClient.query(options);
      const totals = {
        totalCauzes: res.data.eventsForContext.length,
        amountTotal: res.data.eventsForContext.reduce(
          (acc, { current }) => acc + current,
          0,
        ),
        joinTotal: res.data.eventsForContext.reduce(
          (acc, { purchases }) => acc + purchases.length,
          0,
        ),
      };

      cauzeTotals.set(key, totals);
      set({ cauzeTotals });

      return totals;
    } catch (err) {
      if (global.IS_LOCAL_OR_DEV) {
        // eslint-disable-next-line no-console
        console.error(err);
      }
      set({
        cauzesLoading: false,
        isError: true,
        error: err,
      });
    }

    return {};
  },

  getCauzeUrl: (userContext) => {
    if (userContext.companyId) {
      return `/admin/company/${userContext.companyId}/cauzes`;
    }
    if (userContext.charityId) {
      return `/admin/charity/${userContext.charityId}/cauzes`;
    }
    if (userContext.influencerId) {
      return `/admin/influencer/${userContext.influencerId}/cauzes`;
    }
    if (userContext.userId) {
      return `/admin/influencer/${userContext.userId}/cauzes`;
    }
    return `/admin/cauzes`;
  },

  navToCreateCauze: (userContext) => {
    const url = get().getCauzeUrl(userContext);
    navigate(`${url}/create`);
  },

  navToEditCauze: (userContext, cauzeId) => {
    const url = get().getCauzeUrl(userContext);
    navigate(`${url}/${cauzeId}/edit`);
  },

  navToCauzes: () => {
    navigate('/profile/cauzes');
  },

  updatePlannedEventState: async ({ eventId, eventState, index }) => {
    try {
      set((state) => {
        const cauzes = [...state.cauzes];
        if (cauzes[index]) {
          cauzes[index] = { ...cauzes[index], eventState };
        }
        return { cauzes };
      });

      await apolloClient.mutate({
        variables: { eventId, eventState },
        mutation: updatePlannedEventMutation,
        errorPolicy: global.IS_LOCAL_OR_DEV ? 'all' : 'none',
      });
    } catch (err) {
      mixpanel.track('updatePlannedEventState Error', { err: `${err}` });
      set({ error: err, isError: true });
      (err || []).map((errorItem) =>
        uiStore.showNotification({
          body: errorItem.message,
          type: 'ERROR',
        }),
      );
    } finally {
      set({ savingCauze: false });
    }
  },

  updateCauze: (props) => {
    set((state) => ({
      currentCauze: {
        ...state.currentCauze,
        ...props,
      },
    }));

    if (global.IS_LOCAL_OR_DEV) {
      // eslint-disable-next-line no-console
      console.log(useCauzeStore.getState().currentCauze);
    }
  },

  setDateDefaults: (hasDates) => {
    const startDate = hasDates ? moment().utc() : '';
    const endDate = hasDates ? moment().add(EVENT_DEFAULT_DAY_RANGE, 'd') : '';

    get().updateCauze({
      hasDates,
      startDate,
      endDate,
    });
  },

  toggleHasDates: () => {
    const { currentCauze } = get();
    get().setDateDefaults(!currentCauze.hasDates);
  },

  onToggleMenu: (charityId) => {
    set({ menuActiveForCharityId: charityId });
  },

  uploadImage: async (imageFile) => {
    if (!imageFile) return null;

    set({ isUploadingCauzeImage: true });

    try {
      const token = await getToken();
      const data = new FormData();
      data.append(CAUZE_IMAGE_FORM_KEY, imageFile);

      const res = await axios.put(UPLOAD_URL, data, {
        headers: {
          Authorization: token ? `Bearer ${token}` : null,
        },
      });

      get().updateCauze({
        imageId: res.data.id,
        previewImageUrl: res.data.urls.xl,
      });

      setTimeout(() => {
        set({ isUploadingCauzeImage: false });
      }, 2000);

      return {
        imageId: res.data.id,
        previewImageUrl: res.data.urls.xl,
      };
    } catch (err) {
      set({
        isUploadingCauzeImage: false,
        imageUploadError: true,
      });
      return null;
    }
  },

  resetImage: () => {
    set({ imageUploadError: false });
    get().updateCauze({ imageId: null, previewImageUrl: '' });
  },

  createPlannedEvent: async ({ userContext, eventState = 'ACTIVE' }) => {
    set({ savingCauze: true });
    const { currentCauze, videoData } = get();

    const hasMatch =
      currentCauze.hasMatch &&
      currentCauze.userMatchLimit !== 0 &&
      currentCauze.matchTotal !== 0;

    const variables = {
      name: currentCauze.name.trim(),
      charityIds: currentCauze.charities.map((charity) => charity.id),
      description: currentCauze.description,
      previewUrl: currentCauze.previewUrl,
      externalImageUrl: currentCauze.externalImageUrl,
      imageId: currentCauze.imageId,
      hostContexts: userContext,
      eventState,
    };

    if (currentCauze.startDate && currentCauze.endDate) {
      variables.startDate = moment(currentCauze.startDate).format();
      variables.endDate = moment(currentCauze.endDate).format();
    }

    if (hasMatch) {
      variables.userMatchLimit =
        currentCauze.userMatchLimit * currentCauze.matchMultiplier;
      variables.matchTotal = currentCauze.matchTotal;
      variables.matchSponsorContexts = userContext;
      variables.matchType = currentCauze.matchType;
      variables.multiplier = currentCauze.matchMultiplier;
    }

    try {
      const res = await apolloClient.mutate({
        variables,
        mutation: createPlannedEventMutation,
        errorPolicy: global.IS_LOCAL_OR_DEV ? 'all' : 'none',
      });

      if (videoData) {
        try {
          await api2Client.mutate({
            variables: {
              eventId: parseInt(res.data.createPlannedEvent.id, 10),
              videoId: parseInt(videoData.id, 10),
            },
            mutation: updateCauzeMutation,
            errorPolicy: global.IS_LOCAL_OR_DEV ? 'all' : 'none',
          });

          const token = await getToken();
          const data = new FormData();
          data.append('file', videoData.video);

          await axios.post(
            `${UPLOAD_URLV2}/video/${videoData.id}/upload`,
            data,
            {
              headers: {
                Authorization: token ? `Bearer ${token}` : null,
              },
            },
          );
        } catch (videoErr) {
          mixpanel.track('createPlannedEvent video upload error', {
            err: `${videoErr}`,
          });
        }
      }

      if (res?.data?.createPlannedEvent?.id) {
        mixpanel.track('Cauze Created', {
          eventId: res.data.createPlannedEvent.id,
        });
      }

      if (hasMatch) {
        profileStore.updateActiveEntityTotal(currentCauze.matchTotal * -1);
      }

      get().navToCauzes(userContext);
      get().onCreateUnmount();

      if (res.errors) {
        throw res.errors;
      }
    } catch (err) {
      mixpanel.track('createPlannedEvent Error', { err: `${err}` });
      (err || []).map((errorItem) =>
        uiStore.showNotification({
          body: errorItem.message,
          type: 'ERROR',
        }),
      );
      set({ createCauzeError: true, savingCauze: false });
      return;
    }

    set({ savingCauze: false });
  },

  updatePlannedEvent: async ({ eventState = 'ACTIVE', userContext }) => {
    set({ savingCauze: true });
    const { currentCauze, videoData } = get();

    let startDate = currentCauze.startDate;
    let endDate = currentCauze.endDate;
    if (moment.isMoment(startDate) && !startDate.isValid()) startDate = null;
    if (moment.isMoment(endDate) && !endDate.isValid()) endDate = null;

    const variables = {
      name: currentCauze.name,
      charityIds: currentCauze.charities.map((charity) =>
        parseInt(charity.id, 10),
      ),
      description: currentCauze.description,
      previewUrl: currentCauze.previewUrl,
      externalImageUrl: currentCauze.externalImageUrl,
      eventState,
      eventId: parseInt(currentCauze.id, 10),
    };

    if (startDate && endDate) {
      variables.startDate = moment(startDate).format();
      variables.endDate = moment(endDate).format();
    } else {
      variables.startDate = null;
      variables.endDate = null;
    }

    if (Number.isInteger(currentCauze.imageId)) {
      variables.imageId = currentCauze.imageId;
    }

    if (videoData) {
      try {
        variables.videoId = videoData.id;

        const token = await getToken();
        const data = new FormData();
        data.append('file', videoData.video);

        await axios.post(`${UPLOAD_URLV2}/video/${videoData.id}/upload`, data, {
          headers: {
            Authorization: token ? `Bearer ${token}` : null,
          },
        });
      } catch (videoErr) {
        delete variables.videoId;
      }
    }

    try {
      await api2Client.mutate({
        variables,
        mutation: updateCauzeMutation,
        errorPolicy: global.IS_LOCAL_OR_DEV ? 'all' : 'none',
      });

      if (currentCauze.hasMatch) {
        if (currentCauze.hadPreviousMatch) {
          const mutationVariables = {
            id: currentCauze.matchId,
            matchLimit: currentCauze.userMatchLimit,
            matchTotal: currentCauze.matchTotal,
            matchType: currentCauze.matchType,
            multiplier: currentCauze.matchMultiplier,
          };

          const matchTotalDifference = get().getMatchTotalDifference();
          if (matchTotalDifference > 0) {
            mutationVariables.matchTotal = currentCauze.matchTotal;
          }

          await apolloClient.mutate({
            variables: mutationVariables,
            mutation: editMatchMutation,
            errorPolicy: global.IS_LOCAL_OR_DEV ? 'all' : 'none',
          });

          if (matchTotalDifference > 0) {
            profileStore.updateActiveEntityTotal(matchTotalDifference * -1);
          }
        } else {
          await apolloClient.mutate({
            variables: {
              userContext,
              eventId: currentCauze.id,
              matchLimit: currentCauze.userMatchLimit,
              matchTotal: currentCauze.matchTotal,
              matchType: currentCauze.matchType,
              multiplier: currentCauze.matchMultiplier,
            },
            mutation: createMatchMutation,
            errorPolicy: global.IS_LOCAL_OR_DEV ? 'all' : 'none',
          });
          profileStore.updateActiveEntityTotal(currentCauze.matchTotal * -1);
        }
      }

      get().navToCauzes(userContext);
    } catch (err) {
      mixpanel.track('updatePlannedEvent Error', { err: `${err}` });
      (err || []).map((errorItem) =>
        uiStore.showNotification({
          body: errorItem.message,
          type: 'ERROR',
        }),
      );
      set({ createCauzeError: true, savingCauze: false });
      return;
    }

    set({ savingCauze: false });
  },

  loadCauzeToEdit: async ({ userContext, eventId }) => {
    set({ loadingCauze: true, loadingCauzeError: false });

    let options = {
      variables: { eventId: parseInt(eventId, 10) },
      query: eventQueryApi2,
      fetchPolicy: 'no-cache',
      errorPolicy: global.IS_LOCAL_OR_DEV ? 'all' : 'none',
    };

    const result = await api2Client.query(options);

    options = {
      variables: { id: eventId, userContext },
      query: eventQuery,
      fetchPolicy: 'no-cache',
      errorPolicy: global.IS_LOCAL_OR_DEV ? 'all' : 'none',
    };

    const result2 = await apolloClient.query(options);
    const eventMatch = result2.data.event?.matchSummaries?.[0];
    const cauzeToEdit = result.data.event;

    if (!cauzeToEdit) {
      set({ loadingCauzeError: true, loadingCauze: false });
      return;
    }

    let matchToEdit = {};

    if (eventMatch && !userContext.charityId) {
      matchToEdit = { ...eventMatch };
      matchToEdit.userMatchLimit = matchToEdit.matchLimit;
      matchToEdit.matchId = matchToEdit.id;
      matchToEdit.matchMultiplier = matchToEdit.multiplier;

      const sponsorMatch = matchToEdit.matchSponsors.find(
        (matchSponsor) =>
          matchSponsor.id === userContext.companyId ||
          matchSponsor.id === userContext.userId,
      );

      matchToEdit.matchTotal =
        sponsorMatch?.matchTotal || matchToEdit.matchTotal;

      delete matchToEdit.matchLimit;
      delete matchToEdit.id;
    }

    let cauzeDetails = {
      eventState: cauzeToEdit.eventState,
      name: cauzeToEdit.name,
      charities: cauzeToEdit.charities.map((charity) => ({
        ...charity,
        label: charity.name,
        key: charity.name,
        location: [charity.city, charity.state].filter(Boolean).join(', '),
        value: charity.id,
      })),
      previewUrl: cauzeToEdit.previewUrl,
      description: cauzeToEdit.description,
      startDate: cauzeToEdit.startDate
        ? moment(cauzeToEdit.startDate).utc()
        : '',
      endDate: cauzeToEdit.endDate ? moment(cauzeToEdit.endDate).utc() : '',
      previewImageUrl:
        cauzeToEdit.image && cauzeToEdit.image.lg ? cauzeToEdit.image.lg : '',
      id: eventId,
    };

    if (!isEmpty(matchToEdit)) {
      cauzeDetails = {
        ...matchToEdit,
        ...cauzeDetails,
        hadPreviousMatch: true,
        previousMatchTotal: matchToEdit.matchTotal,
        hasMatch: true,
      };
    }

    get().updateCauze(cauzeDetails);

    set((state) => ({
      currentCauze: {
        ...state.currentCauze,
        hasDates: !!cauzeToEdit.startDate || !!cauzeToEdit.endDate,
      },
      loadingCauze: false,
    }));
  },

  getMatchTotalDifference: () => {
    const { currentCauze } = get();
    if (!currentCauze.previousMatchTotal) {
      return currentCauze.matchTotal;
    }
    return currentCauze.matchTotal - currentCauze.previousMatchTotal;
  },

  setMatchHasChanged: (matchHasChanged) => {
    set({ matchHasChanged });
  },

  startVideoUpload: async ({ width, height, video }) => {
    const options = {
      variables: { width, height },
      mutation: createVideoMutation,
      fetchPolicy: 'no-cache',
      errorPolicy: global.IS_LOCAL_OR_DEV ? 'all' : 'none',
    };

    try {
      const result = await api2Client.mutate(options);
      set({
        videoData: {
          width,
          height,
          video,
          id: result.data.createVideo.id,
        },
      });
    } catch (err) {
      mixpanel.track('startVideoUploadError', { err: `${err}` });
      set({ videoData: null });
    }
  },

  onUnmount: () => {
    set({
      isError: false,
      cauzes: [],
      cauzesJoinTotal: 0,
      cauzesAmountTotal: 0,
      cauzesLoading: false,
      isUploadingCauzeImage: false,
      imageUploadError: false,
      menuActiveForCharityId: null,
      videoData: null,
    });
  },

  setLoading: (loading) => {
    set({ isUploadingCauzeImage: loading });
  },

  clearCauzeErrors: () => {
    set({
      loadingCauzeError: false,
      createCauzeError: false,
    });
  },

  onCreateUnmount: () => {
    set({
      currentCauze: createInitialCauze(),
      matchHasChanged: false,
      createCauzeError: false,
      loadingCauze: false,
      loadingCauzeError: false,
      videoData: null,
    });
  },

  deleteCauzeMatch: async ({ eventId }) => {
    await api2Client.mutate({
      variables: { eventId, delete: true },
      mutation: updateCauzeMatchMutation,
    });
  },

  clearMedia: () => {
    set({
      videoData: null,
    });
    get().updateCauze({
      previewImageUrl: '',
      externalImageUrl: null,
      previewUrl: null,
    });
  },
}));

export default useCauzeStore;
